import "../styles/index.scss";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import { differenceBy, throttle } from "lodash-es";
import { BubbleMenu, Toolbar, TooltipProvider } from "../components";
import { RESET_CSS } from "../constants/resetCSS";
import { useLocale } from "../locales";
import { hasExtension } from "../utils/utils";
import { updateCSS } from "../utils/dynamicCSS";
import { getTipTapExtensions } from "./extensions";
import { sequenceNumber } from "../../../revampCRUD/ticketsNforms.crud";
import { InsertVariable } from "../extensions";
import { insertOptions } from "../../TipTapEditor/config/constants";

function RichTextEditor(props, ref) {
	const [extensions, setExtensions] = useState(getTipTapExtensions({ ...props }));
	const { content, useEditorOptions = {} } = props;
	const { t } = useLocale();

	const sortExtensions = useMemo(() => {
		const diff = differenceBy(extensions, extensions, "name");
		const exts = extensions.map(k => {
			const find = extensions.find(ext => ext.name === k.name);
			if (!find) {
				return k;
			}
			return k.configure(find.options);
		});
		return [...exts, ...diff].map((k, i) => k.configure({ sort: i }));
	}, [extensions]);

	const onValueChange = throttle(editor => {
		const output = getOutput(editor, props.output);
		props?.onChangeContent?.(output);
	}, 1000);

	const initialContent = (content ?? "")?.replaceAll("${qrcode}", "https://files.konfhub.com/sample_qrcode.png");
	const editor = useEditor({
		extensions: sortExtensions,
		content: initialContent,
		onUpdate: ({ editor }) => {
			if (onValueChange) onValueChange(editor);
		},
		...useEditorOptions,
	}, [sortExtensions]);

	useImperativeHandle(ref, () => {
		return {
			editor
		};
	});

	useEffect(() => {
		editor?.setEditable(!props?.disabled);
	}, [editor, props?.disabled]);

	useEffect(() => {
		if (props?.resetCSS !== false) {
			updateCSS(RESET_CSS, "react-tiptap-reset");
		}
	}, [props?.resetCSS]);

	useEffect(() => {
		if (editor && content !== undefined) {
			const newContent = content?.replaceAll("${qrcode}", "https://files.konfhub.com/sample_qrcode.png");
			if (editor.getHTML() !== newContent) {
				editor.commands.setContent(newContent);
			}
		}
	}, [content, editor]);

	function replaceQRCode(content) {
		return content.replaceAll('https://files.konfhub.com/sample_qrcode.png', '${qrcode}');
	}

	function getOutput(editor) {
		const html = editor.isEmpty
			? content === null
				? null
				: ""
			: replaceQRCode(editor.getHTML());

		if (!html) return html;

		const regex = /<img((?!width=)[^>])*?>/g;

		const modifiedHtml = html.replace(regex, (match) => {
			if (!match.includes("width=")) {
				return match.replace(/<img/, '<img width="700"');
			}
			return match;
		});

		return modifiedHtml;
	}

	const getSequenceNumbers = async () => {
		try {
			if (!props?.event_id) return;
			const res = await sequenceNumber(props?.event_id);
			if (res.status === 200) {
				const seqNumDetailsFromApi = res.data.sequence_number_details;
				const seqDataForApi = [];
				const seqSetter = {};
				seqNumDetailsFromApi?.forEach((obj) => {
					const seqName = obj?.sequence_name?.toLowerCase().replace(" ", "_");
					seqSetter[obj?.sequence_name] = `{${seqName}}`;
					seqDataForApi.push({
						name: `{${seqName}}`,
						value: `{seq_${obj?.sequence_id.replaceAll("-", "_")}}`
					});
				});

				if (seqDataForApi.length) {
					const updatedExtensions = [...extensions];
					const insertVariableIndex = extensions.findIndex((ext) => ext.name === "insertVariable");
					if (insertVariableIndex !== -1) {
						updatedExtensions.splice(insertVariableIndex, 1);
					}
					const newInsertVariable = InsertVariable.configure({
						variablesList: [{ name: "Sequence Numbers", children: seqDataForApi }, ...insertOptions]
					});
					if (insertVariableIndex !== -1) {
						updatedExtensions.splice(insertVariableIndex, 0, newInsertVariable);
					} else {
						updatedExtensions.push(newInsertVariable);
					}
					setExtensions(updatedExtensions);
					props?.handleSeqDataFromChild(seqDataForApi);
				}
			}
		} catch (err) {
			console.log(err)
		}
	}

	useEffect(() => {
		getSequenceNumbers();
		return () => {
			editor?.destroy?.();
		};
	}, []);

	const hasExtensionValue = hasExtension(editor, "characterCount");

	if (!editor) {
		return <></>;
	}

	return (
		<div className="reactjs-tiptap-editor">
			<TooltipProvider delayDuration={0}>
				<div className="richtext-bg-background richtext-shadow richtext-overflow-hidden richtext-border-custom">
					{!props?.hideBubble && (
						<BubbleMenu
							bubbleMenu={props?.bubbleMenu}
							editor={editor}
							disabled={props?.disabled}
						/>
					)}

					<div className="richtext-flex richtext-flex-col richtext-w-full richtext-max-h-full">
						{!props?.hideToolbar && (
							<Toolbar editor={editor} disabled={!!props?.disabled} />
						)}

						<EditorContent
							className={`richtext-relative ${props?.contentClass || ""}`}
							editor={editor}
						/>

						<div className="richtext-flex richtext-items-center richtext-justify-end richtext-py-1 richtext-px-2 richtext-border-t">
							{hasExtensionValue && (
								<div className="richtext-flex richtext-flex-col">
									<div className="richtext-flex richtext-justify-end richtext-gap-3 richtext-text-sm">
										<span style={{ fontSize: '12px' }}>
											{editor.storage.characterCount.characters()}{" "}
											{t("editor.characters")}
										</span>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</TooltipProvider>
		</div>
	);
}

export default forwardRef(RichTextEditor);